// src/auth/Login.js
import React, { useState, useEffect } from "react";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { useNavigate, Link as RouterLink } from "react-router-dom";
import { API } from "../config/apiConfig";
import { portalGradientTextSx } from "../styles/dashboardBrand";
import { getOrCreateDeviceIdentity } from "../utils/deviceIdentity";

// Accounts that should never be allowed to log in to the portal. We match
// against userId (from the JWT) as well as the typed username/email so a
// blocked user cannot reach the dashboard via any login path. The user
// sees the standard "Invalid username or password" message — there is
// nothing in the UI to hint that the account exists.
const BLOCKED_LOGIN_USER_IDS = new Set([49]);
const BLOCKED_LOGIN_IDENTIFIERS = new Set([
  "playstore9512",
  "playstore.test@is-apps.com",
  "store9835tr3", // UserID 49 — IsMobileOnly = 1 (app-only)
]);

function isBlockedIdentifier(input) {
  if (!input) return false;
  return BLOCKED_LOGIN_IDENTIFIERS.has(String(input).trim().toLowerCase());
}

function isBlockedToken(token) {
  if (!token) return false;
  try {
    const decoded = jwtDecode(token);
    const uid = Number(decoded?.userId ?? decoded?.UserID ?? decoded?.id);
    if (Number.isFinite(uid) && BLOCKED_LOGIN_USER_IDS.has(uid)) return true;
    const username = String(decoded?.username ?? decoded?.UserName ?? "").trim().toLowerCase();
    if (username && BLOCKED_LOGIN_IDENTIFIERS.has(username)) return true;
    const email = String(decoded?.email ?? decoded?.Email ?? "").trim().toLowerCase();
    if (email && BLOCKED_LOGIN_IDENTIFIERS.has(email)) return true;
    return false;
  } catch {
    return false;
  }
}

/** main.Users.IsMobileOnly = 1 → app-only account; web portal must not log in. */
function isMobileOnlyUser(payload) {
  if (!payload || typeof payload !== "object") return false;
  const u = payload.user ?? payload;
  const flag = u.IsMobileOnly ?? u.isMobileOnly;
  return flag === 1 || flag === true || flag === "1";
}

async function fetchProfileIsMobileOnly(token, userId) {
  const id = Number(userId);
  if (!token || !Number.isFinite(id)) return false;
  const profileRes = await axios.get(API.userProfile(id), {
    headers: { Authorization: `Bearer ${token}` },
    timeout: 120000,
  });
  return isMobileOnlyUser(profileRes.data);
}
// Note: we intentionally do NOT clear the dashboard / session profile
// caches here. They are keyed per userId, so leaving them in place
// lets a user who logs back in within the same UTC 6-hour slot reuse
// the cached bundle instead of refetching everything from the server.
import {
  Paper,
  Typography,
  TextField,
  Button,
  Stack,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";

function Login({ onLogin }) {
  const [usernameOrEmail, setUsernameOrEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [inactiveMessage, setInactiveMessage] = useState(""); // 👈 for popup
  const navigate = useNavigate();

  useEffect(() => {
    try {
      getOrCreateDeviceIdentity();
    } catch {
      /* non-fatal */
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError("");
    setInactiveMessage("");
    setLoading(true);

    try {
      localStorage.removeItem("token"); // clear stale token only

      // Short-circuit if the typed username/email is on the blocklist
      // (e.g. the Playstore tester account). Wait ~2 seconds first so
      // the UI looks like it actually contacted the server before
      // showing the same "invalid credentials" message a real failed
      // login would produce — the blocked account is indistinguishable
      // from a wrong password from the user's perspective.
      if (isBlockedIdentifier(usernameOrEmail)) {
        await new Promise((resolve) => setTimeout(resolve, 1000));
        setError("User not found");
        return;
      }

      // Add timeout for Azure SQL cold start (120 seconds for extra safety)
      const res = await axios.post(
        API.login,
        { usernameOrEmail, password, allowGpsBypass: true },
        { 
          headers: { "Content-Type": "application/json" },
          timeout: 120000 // 120 seconds timeout for Azure SQL warm-up
        }
      );

      if (res.data.success) {
        const token = res.data.token;
        // Second layer of defence: even if the user typed a different alias
        // or the blocklist gets out of sync, decode the returned token and
        // reject blocked userIds. The token is never saved in this case.
        if (isBlockedToken(token)) {
          setError("User not found");
          return;
        }
        if (isMobileOnlyUser(res.data)) {
          setError("User not found");
          return;
        }
        const userId =
          res.data.userId ??
          (() => {
            try {
              return jwtDecode(token)?.userId ?? jwtDecode(token)?.UserID;
            } catch {
              return null;
            }
          })();
        try {
          if (await fetchProfileIsMobileOnly(token, userId)) {
            setError("User not found");
            return;
          }
        } catch (profileErr) {
          console.warn("Login profile check failed:", profileErr?.message || profileErr);
        }
        localStorage.setItem("token", token);
        onLogin();
        navigate("/dashboard");
      } else {
        setError(res.data.error || "Login failed");
      }
    } catch (err) {
      console.error("❌ Login error:", err.response?.data || err.message);

      if (err.response?.status === 403) {
        // 👇 backend returned inactive account
        setInactiveMessage(
          err.response?.data?.error ||
            "Your account is inactive. Please contact support."
        );
      } else if (err.code === 'ECONNABORTED' || err.message?.includes('timeout')) {
        // 👇 timeout error - likely Azure SQL taking too long
        setError(
          "⏱️ Connection timeout (2 min). The database is waking up. Please click Login again to retry."
        );
      } else if (err.code === 'ERR_NETWORK' || !err.response) {
        // 👇 network error - server might be down
        setError(
          "🔌 Cannot connect to server. Please check your internet connection or try again later."
        );
      } else if (isBlockedIdentifier(usernameOrEmail)) {
        setError("User not found");
      } else {
        setError(
          err.response?.data?.error ||
            err.response?.data?.message ||
            err.message ||
            "❌ Server error while logging in"
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Stack
      sx={{
        minHeight: "100vh",
        alignItems: "center",
        justifyContent: "center",
        background: 'linear-gradient(135deg, #4FACFE 0%, #6366F1 40%, #9333EA 72%, #E11D48 100%)',
        position: 'relative',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'radial-gradient(circle at 20% 80%, rgba(120, 119, 198, 0.3) 0%, transparent 50%), radial-gradient(circle at 80% 20%, rgba(255, 119, 198, 0.3) 0%, transparent 50%), radial-gradient(circle at 40% 40%, rgba(120, 219, 255, 0.2) 0%, transparent 50%)',
          pointerEvents: 'none',
        },
      }}
    >
      <Paper 
        elevation={24} 
        sx={{ 
          p: 5, 
          width: "100%", 
          maxWidth: 450,
          borderRadius: 3,
          bgcolor: 'background.paper',
          backdropFilter: 'blur(20px)',
          position: 'relative',
          zIndex: 1,
        }}
      >
        <Typography
          variant="h4"
          gutterBottom
          textAlign="center"
          sx={portalGradientTextSx({
            fontWeight: 700,
            mb: 3,
          })}
        >
          Welcome Back
        </Typography>
        <Typography 
          variant="h6" 
          textAlign="center" 
          color="text.secondary"
          sx={{ mb: 4 }}
        >
          Admin Portal
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleLogin}>
          <Stack spacing={2}>
            <TextField
              label="Username or Email"
              value={usernameOrEmail}
              onChange={(e) => setUsernameOrEmail(e.target.value)}
              required
              fullWidth
              disabled={loading}
            />

            <TextField
              label="Password"
              type={showPassword ? "text" : "password"}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              fullWidth
              disabled={loading}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword((prev) => !prev)}
                      edge="end"
                      disabled={loading}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              variant="contained"
              size="large"
              disabled={loading}
            >
              {loading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                "Login"
              )}
            </Button>

            {/* Loading message with Azure SQL info */}
            {loading && (
              <Alert severity="info" sx={{ mt: 2 }}>
                <Typography variant="body2">
                  Connecting to server... This may take up to 2 minutes if the database is waking up from idle state. Please be patient.
                </Typography>
              </Alert>
            )}

            {/* ✅ Forgot Password link */}
            <Typography variant="body2" textAlign="center">
              <Link component={RouterLink} to="/forgot-password" underline="hover">
                Forgot Password?
              </Link>
            </Typography>

            <Typography variant="body2" textAlign="center">
              Don't have an account?{" "}
              <Link component={RouterLink} to="/signup" underline="hover">
                Sign up
              </Link>
            </Typography>

          </Stack>
        </form>
      </Paper>

      {/* 👇 Inactive User Popup */}
      <Dialog
        open={Boolean(inactiveMessage)}
        onClose={() => setInactiveMessage("")}
      >
        <DialogTitle>Account Inactive</DialogTitle>
        <DialogContent>
          <DialogContentText>{inactiveMessage}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInactiveMessage("")} autoFocus>
            OK
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}

export default Login;
